<template>
  <section>
    <v-container>
      <v-row>
        <v-col class="col-8 px-6">
          <booking-date-picker
            ref="datePicker"
            :initial-date-range="dateRange"
            @selected="dateRangeChanged"
          />
        </v-col>

        <v-col class="col-4">
          <v-row>
            <v-col class="col-12 d-flex justify-end pt-0">
              <enter-booking-btn class="mr-4" />
            </v-col>
            <v-col class="col-12 d-flex justify-end">
              <v-btn
                class="mr-4"
                :disabled="bookingEntries.length === 0"
                color="primary"
                outlined
                large
                @click="downloadExcel"
              >
                <i class="fad fa-file-spreadsheet mr-2" />
                {{ $t('bookingComplete.excelDownload') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="col-12">
          <v-data-table
            :headers="headers"
            :items="bookingEntries"
            :items-per-page="15"
            :no-data-text="$t('dataTable.noDataAvailable')"
            :no-results-text="$t('dataTable.noResultFound')"
            :footer-props="{ itemsPerPageText: $t('general.rowsPerPage') }"
            :loading="isLoading"
            :loading-text="$t('general.loadingData')"
            :custom-sort="sortTable"
            :options="options"
            @update:options="updateOptions"
          >
            <template slot="header" slot-scope="props">
              <tr class="grey lighten-3">
                <th
                  v-for="header in props.props.headers"
                  :key="header.text"
                  class="pa-0 pr-1"
                >
                  <div v-if="filters.hasOwnProperty(header.value)">
                    <v-select
                      :value="filters[header.value]"
                      hide-details
                      outlined
                      small
                      dense
                      clearable
                      item-text="text"
                      item-value="value"
                      :items="columnValueList(header.value)"
                      class="mt-0"
                      @input="(newFilter) => updateFilter(newFilter, header)"
                    />
                  </div>
                </th>
              </tr>
            </template>
            <template #item="props">
              <tr class="clickable">
                <td @click="goToBooking(props.item.booking.getId())">
                  {{ $t(props.item.productTranslation) }}
                </td>
                <td @click="goToBooking(props.item.booking.getId())">
                  {{ $t(props.item.productDefinitionTranslation) }}
                </td>

                <!-- customer -->
                <td @click="goToBooking(props.item.booking.getId())">
                  <span v-if="props.item.booking">
                    <p v-if="props.item.booking.user.getCompany()" class="my-0">
                      {{ props.item.booking.user.getCompany() }}
                    </p>
                    {{ props.item.booking.user.getFullName() }}
                  </span>
                </td>

                <!-- date -->
                <td @click="goToBooking(props.item.booking.getId())">
                  {{ props.item.getStartDate() | formatDate }}
                </td>

                <!-- allocation number -->
                <td>
                  <v-row>
                    <v-col class="col-8 py-0">
                      <v-text-field
                        v-model="props.item.allocationNumber"
                        class="mt-6"
                        dense
                        type="number"
                        outlined
                        @keydown.enter="props.item.saveAllocationNumber()"
                      />
                    </v-col>

                    <v-col class="col-4">
                      <v-btn
                        x-small
                        outlined
                        class="mt-5"
                        @click="props.item.saveAllocationNumber()"
                      >
                        <span class="fal fa-save" />
                      </v-btn>
                    </v-col>
                  </v-row>
                </td>
                <td @click="goToBooking(props.item.booking.getId())">
                  <span v-if="props.item.booking">
                    {{ props.item.booking.getBookingStates().getStateText() }}
                  </span>
                </td>

                <!-- payment state -->
                <td @click="goToBooking(props.item.booking.getId())">
                  <!-- yes, it's paid -->
                  <span
                    v-if="props.item.booking && props.item.booking.isPaid()"
                    style="white-space: nowrap"
                  >
                    <span class="fal fa-check mr-2 green--text" />
                    {{ $t('shopBackendBookingDetail.paid') }}
                  </span>

                  <!-- unpaid booking -->
                  <span v-else-if="props.item.booking">
                    <span class="fal fa-exclamation-triangle mr-2 red--text" />
                    {{ $t('bookingByCustomerSearch.unpaid') }}
                  </span>
                </td>
                <td @click="goToBooking(props.item.booking.getId())">
                  <span v-if="props.item.booking">
                    {{ props.item.booking.note }}
                  </span>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import EnterBookingBtn from './EnterBookingBtn'
import _ from 'lodash'
import BookingEntryFilterable from '../../classes-shared/bookings/BookingEntryFilterable'
import XLSX from 'xlsx'
import XlsxHelper from '../../classes-shared/XLSX/XlsxHelper'
import BookingDatePicker from './BookingDatePicker'

export default {
  name: 'BookingComplete',
  components: { BookingDatePicker, EnterBookingBtn },
  props: {
    filters: {
      required: true,
      type: Object,
    },
    // contains also the page
    options: {
      required: true,
      type: Object,
    },
    dateRangeInput: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      bookingsEntryHandler: new BookingEntryFilterable({}),
      initialPageLoadDone: false,
      bookingEntries: [],
      dateFormat: 'YYYY-MM-DDTHH:mm:ss.SS',
      isLoading: false,
      // https://codepen.io/fontzter/pen/qywQjK
      headers: [
        {
          text: this.$t('shopBackendBookingComplete.product'),
          align: 'left',
          sortable: true,
          value: 'product',
        },
        {
          text: this.$t('shopBackendBookingComplete.productDefinition'),
          align: 'left',
          sortable: true,
          value: 'productDefinition',
        },
        {
          text: this.$t('shopBackendBookingComplete.customer'),
          align: 'left',
          sortable: true,
          value: 'customer',
        },
        {
          text: this.$t('general.date'),
          align: 'left',
          sortable: true,
          value: 'startDate',
        },
        {
          text: this.$t('bookingsComplete.roomNumber'),
          align: 'left',
          sortable: true,
          value: 'allocationNumber',
        },
        {
          text: this.$t('shopBackendBookingComplete.bookingState'),
          align: 'left',
          sortable: false,
          value: 'bookingState',
        },
        {
          text: this.$t('shopBackendBookingDetail.paymentState'),
          align: 'left',
          sortable: false,
          value: 'paymentState',
        },
        {
          text: this.$t('shopBackendBookingComplete.remark'),
          align: 'left',
          sortable: false,
          value: 'remark',
        },
      ],
    }
  },
  computed: {
    dateRange: {
      get() {
        return this.dateRangeInput
      },
      set(value) {
        this.$emit('update:dateRange', value)
      },
    },
  },
  watch: {
    filters: {
      deep: true,
      async handler() {
        await this.onFilterOrDateChange()
      },
    },
    // responsible to store the pagination store
    bookingEntries: {
      deep: true,
      handler() {
        if (!this.initialPageLoadDone) {
          this.$emit('update:options')
          this.initialPageLoadDone = true
        }
      },
    },
  },
  methods: {
    updateFilter(newFilter, header) {
      const tmpFilter = Object.assign({}, this.filters)
      tmpFilter[header.value] = newFilter
      this.$emit('update:filters', tmpFilter)
    },
    // responsible to store the pagination store
    updateOptions(optionsObject) {
      if (this.initialPageLoadDone) {
        this.$emit('update:options', optionsObject)
      }
    },

    goToBooking(bookingId) {
      this.$router.push({
        name: 'bookingDetailView',
        params: { id: bookingId },
      })
    },

    columnValueList(val) {
      if (this.bookingEntries.length > 0) {
        if (val === 'customer') {
          return this.bookingEntries.flatMap((bookingEntry) =>
            bookingEntry.booking.user.getFullName()
          )
        }

        if (val === 'bookingState') {
          // not supported by api yet
          return _.uniqBy(
            this.bookingEntries.flatMap((bookingEntry) => {
              if (bookingEntry.booking.getBookingStates().getStateText()) {
                return {
                  text: bookingEntry.booking.getBookingStates().getStateText(),
                  value: bookingEntry.booking
                    .getBookingStates()
                    .getBookingState().state,
                }
              }
            }),
            'text'
          )
        }

        if (val === 'productDefinition') {
          return _.uniqBy(
            this.bookingEntries.flatMap((bookingEntry) => {
              /* global i18n */
              return {
                text: i18n.t(bookingEntry.productDefinitionTranslation),
                value: bookingEntry,
              }
            }),
            'text'
          )
        }

        if (val === 'product') {
          return _.uniqBy(
            this.bookingEntries.flatMap((bookingEntry) => {
              /* global i18n */
              return {
                text: i18n.t(bookingEntry.productTranslation),
                value: bookingEntry,
              }
            }),
            'text'
          )
        }

        if (val === 'paymentState') {
          return _.uniqBy(
            this.bookingEntries.flatMap((bookingEntry) => {
              let text = i18n.t('bookingByCustomerSearch.unpaid')
              if (bookingEntry.booking.isPaid()) {
                text = i18n.t('shopBackendBookingDetail.paid')
              }
              return {
                text: text,
                value: bookingEntry,
              }
            }),
            'text'
          )
        }
      }
    },

    getProductIdFilterVal() {
      if (this.filters['productDefinition']) {
        return this.filters['productDefinition'].getProductId()
      }
      return this.filters['product'] ? this.filters['product'].productId : null
    },
    applyOfflineFilters() {
      if (this.filters['customer']) {
        this.bookingEntries = this.bookingEntries.filter((bookingEntry) => {
          return (
            bookingEntry.booking.user.getFullName() === this.filters['customer']
          )
        })
      }

      if (this.filters['paymentState']) {
        const state = this.filters['paymentState']
        this.bookingEntries = this.bookingEntries.filter((bookingEntry) => {
          return bookingEntry.booking.isPaid() === state.booking.isPaid()
        })
      }

      if (this.filters['time']) {
        const filterTime = this.filters['time'].getBoundaryDate()
        this.bookingEntries = this.bookingEntries.filter((bookingEntry) => {
          return bookingEntry.getBoundaryDate() === filterTime
        })
      }
    },
    async onFilterOrDateChange() {
      this.isLoading = true
      this.bookingEntries = await this.bookingsEntryHandler.loadFilteredBookingEntries(
        this.$options.filters.formatDate(this.dateRange.from, this.dateFormat),
        this.$options.filters.formatDate(this.dateRange.to, this.dateFormat),
        this.getProductIdFilterVal(),
        this.filters['productDefinition']
          ? this.filters['productDefinition'].productDefinitionId
          : null,
        //@todo not working on api yet
        null,
        this.filters['bookingState'] ? this.filters['bookingState'] : null
      )

      this.applyOfflineFilters()
      this.isLoading = false
    },
    async downloadExcel() {
      EventBus.$emit('spinnerShow')
      let workBook = XLSX.utils.book_new()
      await XLSX.utils.book_append_sheet(
        workBook,
        await this.getWorkSheet(),
        this.$t('bookingComplete.bookings')
      )
      EventBus.$emit('spinnerHide')
      XLSX.writeFile(workBook, this.$t('bookingComplete.bookings') + '.xlsx')
    },
    async getWorkSheet() {
      let jsonWorksheet = []
      this.bookingEntries.forEach((bookingEntry) => {
        jsonWorksheet.push({
          product: this.$t(bookingEntry.productTranslation),
          productDefinition: this.$t(bookingEntry.productDefinitionTranslation),
          customer: bookingEntry.booking?.user?.getFullName(),
          mail: bookingEntry.booking?.user?.getMail(),
          time: bookingEntry.getBoundaryDate(),
          bookingState: bookingEntry.booking?.getBookingStates().getStateText(),
          remark: bookingEntry.booking?.note,
        })
      })
      let workSheet = XLSX.utils.json_to_sheet(jsonWorksheet)
      let xlsxHelper = new XlsxHelper(workSheet)
      await xlsxHelper.setHeader([
        this.$t('shopBackendBookingComplete.product'),
        this.$t('shopBackendBookingComplete.productDefinition'),
        this.$t('shopBackendBookingComplete.customer'),
        this.$t('shopBackendBookingComplete.mail'),
        this.$t('shopBackendBookingComplete.time'),
        this.$t('shopBackendBookingComplete.bookingState'),
        this.$t('shopBackendBookingComplete.remark'),
      ])

      // set auto width
      await xlsxHelper.setColumnWidthAuto()
      return xlsxHelper.getWorkSheet()
    },
    sortTable(items, sortBy, sortDesc) {
      if (sortBy.length > 0) {
        if (sortBy[0] === 'customer') {
          return this.bookingEntries.sort((a, b) => {
            return this.compareStrings(
              a.booking?.user?.getFullName(),
              b.booking?.user?.getFullName(),
              sortDesc[0]
            )
          })
        } else if (sortBy[0] === 'product') {
          return this.bookingEntries.sort((a, b) => {
            return this.compareStrings(
              this.$t(a.productTranslation),
              this.$t(b.productTranslation),
              sortDesc[0]
            )
          })
        } else if (sortBy[0] === 'productDefinition') {
          return this.bookingEntries.sort((a, b) => {
            return this.compareStrings(
              this.$t(a.productDefinitionTranslation),
              this.$t(b.productDefinitionTranslation),
              sortDesc[0]
            )
          })
        }
      }

      return items
    },
    compareStrings(a, b, desc) {
      if (a < b) {
        if (desc) {
          return -1
        }
        return 1
      }
      if (a > b) {
        if (desc) {
          return 1
        }
        return -1
      }
      return 0
    },
    dateRangeChanged(dateRange) {
      this.dateRange = dateRange
    },
  },
}
</script>

<style scoped lang="scss">
> .v-calendar-weekly__head-weekday {
  padding-top: 1rem !important;
}

>>> .v-date-picker-table {
  height: auto;
  padding-bottom: 0.5rem;
}
>>> thead > tr > th {
  padding: 0px;
}
</style>
